/**
 * Bixie haircuts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bixieHaircutsPack: ToilePack = {
    slug: 'bixie-haircuts',
    category: 'beauty',
    name: 'Bixie haircuts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'sleek', label: 'Sleek', subject: 'a sleek bixie with a short back and ear-length sides, side-swept fringe'},
        {key: 'shaggy', label: 'Shaggy', subject: 'a shaggy bixie with choppy layers and texture'},
        {key: 'curly', label: 'Curly', subject: 'a curly bixie with defined curls around the ears'},
    ],
    styles: [
        {key: 'salon', label: 'Salon', style: 'photographed in a bright hair salon by soft broad window daylight from camera-left, a light and open balance with few dark tones', palette: 'warm white walls and pale wood, the hair the darkest tone', framing: 'three-quarter view at eye level, the salon mirror soft behind'},
        {key: 'street', label: 'Street', style: 'photographed outdoors on a city street in low late-afternoon sun from camera-right grazing the hair, a medium balance with soft shadow on the far cheek', palette: 'warm brick and stone with muted greens', framing: 'eye-level head-and-shoulders, shallow depth of field, the street soft behind'},
        {key: 'editorial', label: 'Editorial', style: 'a beauty editorial portrait against a painted plaster wall, soft even light from above and slightly frontal, a mid-tone balance with gentle shadow under the jaw', palette: 'dusty rose plaster, the hair in clear contrast', framing: 'near-frontal head-and-shoulders, centred with space above the crown'},
    ],
    directions: ['silver grey', 'with glasses', 'copper red'],
    directionHint: 'try: silver grey',
    seo: {
        title: 'Bixie Haircuts: Sleek, Shaggy, Curly',
        metaDescription: 'Nine pictures of the bixie, the cut between a bob and a pixie: a sleek, a shaggy and a curly version, in a salon, on a street and in editorial.',
        h1: 'Bixie haircuts: sleek, shaggy, curly',
        tagline: 'Nine pictures, three bixies, each in a salon, on a city street and in an editorial portrait.',
        intro: 'The bixie sits between a bob and a pixie, with a short back, ear-length sides and a fringe. These pictures show a sleek bixie with a side-swept fringe, a shaggy bixie with choppy layers, and a curly bixie with defined curls around the ears. Each is seen in a bright salon, on a city street and in a beauty editorial portrait. You can type a change, such as silver grey, to ask for the cut with it.',
        useCases: [
            'a reference for the length at the back and sides',
            'choosing a sleek, shaggy or curly bixie',
            'a wall print for a salon',
            'a profile picture',
        ],
        faq: [
            {q: 'How is a bixie different from a pixie?', a: 'A bixie is longer than a pixie and shorter than a bob, with a short back and ear-length sides.'},
            {q: 'What is the difference between the three bixies?', a: 'The sleek bixie has a side-swept fringe. The shaggy one has choppy layers. The curly one has defined curls around the ears.'},
            {q: 'Can I change the colour?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
